import React from "react";
import { css, SerializedStyles } from "@emotion/react";
import Link from "next/link";
import { colorVars } from "../../utils/colorUtils";
import { IconPosition } from "./Button";
import {
  GTMEventName,
  GTMEvent,
  pushGtmEvent,
} from "apps/dashboard-client/utils/googleTagManagerUtils";
import { useRouter } from "next/router";

type NavLinkProps = {
  text: string;
  openInNewTab?: boolean;
  href?: string;
  onClick?: () => void;
  disabled?: boolean;
  icon?: React.ReactElement;
  customCss?: SerializedStyles;
  iconPosition?: IconPosition;
  customGtmEvent?: GTMEvent;
  dataCy?: string;
};

const s = {
  link: css({
    textDecoration: "none",
    color: colorVars.bluePrimary,
    width: "fit-content",
    padding: "0 4px",
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: "transparent",
    outline: "none",
    textAlign: "start",
    display: "flex",
    alignItems: "center",
    columnGap: ".6rem",
    fontFamily: "OpenSans, sans serif",
    backgroundColor: "transparent",
    fontSize: "1.6rem",
    cursor: "pointer",
    ":hover": {
      textDecoration: "underline",
      textUnderlineOffset: 1,
    },
    ":active": {
      borderColor: colorVars.bluePrimary,
    },
    ":focus": {
      borderColor: colorVars.bluePrimary,
    },
  }),
  disabled: {
    color: colorVars.gray2,
    pointerEvents: "none" as const,
    cursor: "initial",
    ":hover": {
      textDecoration: "none",
    },
    ":active": {
      borderColor: "transparent",
    },
    ":focus": {
      borderColor: "transparent",
    },
  },
};
const NavLink = ({
  text,
  href,
  onClick,
  openInNewTab,
  icon,
  disabled,
  customCss,
  dataCy,
  customGtmEvent,
  iconPosition = IconPosition.RIGHT,
}: NavLinkProps) => {
  const router = useRouter();
  return href ? (
    <Link
      onClick={(e) => {
        e.stopPropagation();
        if (onClick) {
          onClick();
        }
        pushGtmEvent(
          customGtmEvent || {
            event: GTMEventName.NAV_ITEM_CLICKED,
            resource: null,
            data: {
              from: router.pathname,
              to: href,
              linkText: text,
            },
          }
        );
      }}
      target={openInNewTab ? "_blank" : "_self"}
      css={[s.link, disabled && s.disabled, customCss]}
      href={href}
      data-cy={dataCy}
      aria-disabled={disabled}
    >
      {icon && iconPosition === IconPosition.LEFT && icon}
      {text}
      {icon && iconPosition === IconPosition.RIGHT && icon}
    </Link>
  ) : (
    <button
      css={[s.link, disabled && s.disabled, customCss]}
      data-cy={dataCy}
      onClick={(e) => {
        e.stopPropagation();
        pushGtmEvent(
          customGtmEvent || {
            event: GTMEventName.NAV_ITEM_CLICKED,
            resource: null,
            data: { buttonText: text },
          }
        );
        if (onClick) {
          onClick();
        }
      }}
      aria-disabled={disabled}
    >
      {icon && iconPosition === IconPosition.LEFT && icon}
      {text}
      {icon && iconPosition === IconPosition.RIGHT && icon}
    </button>
  );
};

export default NavLink;
